/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useContext, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'

// Components
import {Accordion, Box, Flex, Text} from '@chakra-ui/react'
import StoresList from './stores-list'

// Others
import {
    STORE_LOCATOR_DISTANCE,
    STORE_LOCATOR_NUM_STORES_PER_LOAD,
    STORE_LOCATOR_DISTANCE_UNIT,
    STORE_LOCATOR_HREF,
    NUM_STORES_PER_REQUEST_API_MAX,
    DEFAULT_STORE_LOCATOR_POSTAL_CODE
} from '../../constants'

// Hooks
import {useSearchStores} from '@salesforce/commerce-sdk-react'

import {StoreLocatorContext} from './index'
import FindStoreLink from '../find-store/find-store-link'
import {useLocation} from 'react-router-dom'
import {useGeocodeAddress} from '../../google-maps-api'
import {useCountry} from '../../hooks'
import useMultiSite from '../../hooks/use-multi-site'
import useNavigation from '../../hooks/use-navigation'

const StoreLocatorContent = () => {
    const {searchStoresParams, setSearchStoresParams, setUserHasSetManualGeolocation} =
        useContext(StoreLocatorContext)
    const {countryCode, address, latitude, longitude, limit} = searchStoresParams
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const [alreadySearched, setAlreadySearched] = useState(false)
    const location = useLocation()
    const currentCountryCode = useCountry()
    const navigate = useNavigation()

    // update searchStoresParams based on URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const latitude = params.get('latitude') ? Number(params.get('latitude')) : null
        const longitude = params.get('longitude') ? Number(params.get('longitude')) : null
        const address = params.get('address')

        if (currentCountryCode) {
            // Reset to defaults when URL parameters are removed
            if (latitude == null && longitude == null && !address) {
                setSearchStoresParams({
                    countryCode: currentCountryCode,
                    postalCode: DEFAULT_STORE_LOCATOR_POSTAL_CODE,
                    limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
                })
                setAlreadySearched(false)
            } else {
                setSearchStoresParams({
                    latitude,
                    longitude,
                    address,
                    countryCode: currentCountryCode,
                    limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
                })
                setUserHasSetManualGeolocation(true)
            }
        }
    }, [currentCountryCode, location.search, setSearchStoresParams, setUserHasSetManualGeolocation])

    // geocode address if latitude and longitude are not provided
    const {coords} = useGeocodeAddress(
        address && (latitude == null || longitude == null) ? address : null,
        currentCountryCode
    )
    // update searchStoresParams with geocoded coordinates
    useEffect(() => {
        if (address && coords && currentCountryCode && (latitude == null || longitude == null)) {
            setSearchStoresParams({
                latitude: coords.latitude,
                longitude: coords.longitude,
                address,
                countryCode: currentCountryCode,
                limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
            })
        }
    }, [address, coords, currentCountryCode, latitude, longitude, setSearchStoresParams])

    const [numStoresToShow, setNumStoresToShow] = useState(limit)

    // Either the countryCode & postalCode or latitude & longitude are defined, never both
    // If coordinates are passed, the values for country and postal code are ignored.
    const {
        data: searchStoresData,
        isLoading,
        refetch,
        isFetching
    } = useSearchStores(
        {
            parameters: {
                countryCode: countryCode,
                // postalCode: postalCode, we don't have the mapping?
                latitude: latitude,
                longitude: longitude,
                locale: intl.locale,
                maxDistance: STORE_LOCATOR_DISTANCE,
                limit: NUM_STORES_PER_REQUEST_API_MAX,
                distanceUnit: STORE_LOCATOR_DISTANCE_UNIT
            }
        },
        {
            enabled: Boolean(countryCode && latitude != null && longitude != null)
        }
    )

    const storesInfo = useMemo(
        () =>
            isLoading || isFetching
                ? undefined
                : searchStoresData?.data?.slice(0, numStoresToShow) || [],
        [isLoading, isFetching, searchStoresData, numStoresToShow]
    )
    const numStores = searchStoresData?.total || 0

    // FindStoreLink provides the coordinates
    const submitForm = (formData) => {
        setAlreadySearched(true)
        setNumStoresToShow(STORE_LOCATOR_NUM_STORES_PER_LOAD)

        const newSearch = `?${new URLSearchParams(formData).toString()}`
        if (location.search === newSearch) {
            // If the search parameters are the same as the current URL, we refetch to update the stores
            refetch()
        } else {
            navigate(`${buildUrl(STORE_LOCATOR_HREF)}${newSearch}`)
        }
    }

    const isLoadMoreButtonShowed =
        numStoresToShow < numStores && numStoresToShow < NUM_STORES_PER_REQUEST_API_MAX

    const onLoadMoreClicked = () => {
        setNumStoresToShow(
            numStoresToShow + STORE_LOCATOR_NUM_STORES_PER_LOAD <= NUM_STORES_PER_REQUEST_API_MAX
                ? numStoresToShow + STORE_LOCATOR_NUM_STORES_PER_LOAD
                : numStoresToShow
        )
    }

    return (
        <>
            <FindStoreLink
                inputProps={{
                    'aria-label': intl.formatMessage({
                        defaultMessage: 'Your location',
                        id: 'use_find_store_fields.placeholder.location'
                    })
                }}
                onSubmit={submitForm}
            />
            <Flex pt="4">
                <Box width="100%">
                    <Accordion allowMultiple flex={[1, 1, 1, 5]}>
                        {storesInfo?.length > 0 && (
                            <StoresList
                                storesInfo={storesInfo?.map((store) => ({
                                    ...store,
                                    infoWindowHtml: `<strong>${store?.name ?? ''}</strong><p>${
                                        store?.address1 ?? ''
                                    } ${store?.city ?? ''} ${store?.stateCode ?? ''}</p>`
                                }))}
                                isLoadMoreButtonShowed={isLoadMoreButtonShowed}
                                onLoadMoreClicked={onLoadMoreClicked}
                            />
                        )}
                        <Box role="status">
                            {alreadySearched && !storesInfo?.length && (
                                <Text>
                                    {intl.formatMessage({
                                        defaultMessage:
                                            'Sorry, there are no locations in this area',
                                        id: 'store_locator.description.no_locations'
                                    })}
                                </Text>
                            )}
                        </Box>
                    </Accordion>
                </Box>
            </Flex>
        </>
    )
}

StoreLocatorContent.propTypes = {}

export default StoreLocatorContent
